import VueI18n, { IVueI18n } from 'vue-i18n';
import { IDate, LanguageEnum, TicketStatusEnum } from '@/api/types';

// date utils, date string format YYYY-MM-DD
export const formatDate = (date: Date | null, useLocalDate: boolean = true) => {
  if (date) {
    if (useLocalDate) {
      const month = date.getMonth() + 1;
      return `${date.getFullYear()}-${month >= 10 ? month : '0' + month}-${
        date.getDate() >= 10 ? date.getDate() : '0' + date.getDate()
      }`;
    } else {
      return date.toISOString().slice(0, 10);
    }
  } else {
    return '';
  }
};

export const stringToIDate = (str: string | null) => {
  if (str && str.split('-').length === 3) {
    const arr = str.split('-');
    return {
      year: stringToNumber(arr[0]),
      month: stringToNumber(arr[1]),
      day: stringToNumber(arr[2]),
    } as IDate;
  } else {
    return null;
  }
};

export const stringFromIDate = (date: IDate | null) => {
  if (date) {
    const month = date.month >= 10 ? `${date.month}` : `0${date.month}`;
    const day = date.day >= 10 ? `${date.day}` : `0${date.day}`;
    return `${date.year}-${month}-${day}`;
  } else {
    return null;
  }
};

export const dateToIDate = (date: Date, useLocalDate: boolean = true) =>
  stringToIDate(formatDate(date, useLocalDate));

// string utils
export const uppercaseToDash = (str: string) =>
  str
    .replace(/(^[A-Z])/, ([first]) => first.toLowerCase())
    .replace(/([A-Z])/g, ([letter]) => `-${letter.toLowerCase()}`);

export const randomString = (length: number) =>
  Math.random()
    .toString(36)
    .substring(2, length) +
  Math.random()
    .toString(36)
    .substring(2, length);

export const stringHashCode = (str: string) => {
  let hash = 0;
  let chr;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const stringToNumber = (value: string | number) => {
  if (typeof value != 'number') {
    return parseInt((value || '').toString(), 10);
  }
  return value;
};

export const compareString = (
  a: string,
  b: string,
  whenEqual?: (a: string, b: string) => number
) => (a < b ? -1 : a > b ? 1 : whenEqual === void 0 ? 0 : whenEqual(a, b));

// i18n utils
export const translateComponentResource = (
  $i18n: VueI18n & IVueI18n,
  componentName: string,
  resource: string,
  options?: { [key: string]: any }
) => {
  const dashName = uppercaseToDash(componentName);
  if ($i18n.te(`@comps.${dashName}.${resource}`)) {
    return $i18n.t(`@comps.${dashName}.${resource}`, options);
  }
  return resource;
};

export const translateViewResource = (
  $i18n: VueI18n & IVueI18n,
  componentName: string,
  resource: string,
  options?: { [key: string]: any }
) => {
  const dashName = uppercaseToDash(componentName);
  if ($i18n.te(`@views.${dashName}.${resource}`)) {
    return $i18n.t(`@views.${dashName}.${resource}`, options);
  }
  return resource;
};

export const translatePluralResource = (
  $i18n: VueI18n & IVueI18n,
  resource: string,
  n: number
) => {
  if ($i18n.te(`@plural.${resource}`)) {
    return $i18n.tc(`@plural.${resource}`, n);
  }
  return resource;
};

export const supportedTravelerLangs = [
  LanguageEnum.ENGLISH,
  LanguageEnum.JAPANESE,
  LanguageEnum.CHINESE,
  LanguageEnum.CHINESE_T,
  LanguageEnum.KOREAN,
];

export const supportedTicketStatus = [
  TicketStatusEnum.UNKNOWN,
  TicketStatusEnum.IN_USE,
  TicketStatusEnum.USED,
];

export const supportedMogiriSettingPlanLocaledExtendedInfoLangs = [
  LanguageEnum.ENGLISH,
  LanguageEnum.JAPANESE,
  LanguageEnum.CHINESE,
  LanguageEnum.CHINESE_T,
  LanguageEnum.KOREAN,
];

// file utils
export const getFilenameWithoutSuffixFromUrl = (url: string) => {
  if (url) {
    var m = url.toString().match(/.*\/(.+?)\./);
    if (m && m.length > 1) {
      return m[1];
    }
  }
  return '';
};

export const downloadFileFromUrl = (
  url: string,
  filename: string,
  contentType: string = 'text/csv'
) => {
  const link = document.createElement('a');
  link.id = 'dummytestlink';
  link.href = url;
  link.download = filename;
  link.click();
  link.remove();
};

// error utils
export const formatApiError = ($i18n: VueI18n & IVueI18n, err: any) => {
  const common = err.data && err.data.common ? err.data.common : null;
  if (common && common.statusCode) {
    if (stringToNumber(common.statusCode) === 403) {
      return $i18n.t('Permission denied');
    } else if (common.errorDescription) {
      return `code: ${common.statusCode}, error: ${common.errorDescription}`;
    }
  }
  return JSON.stringify(err);
};

// color utils
export const foregroundColor = (backgroundColor: string) => {
  const effetiveBackgroundColor = backgroundColor.startsWith('#')
    ? backgroundColor.length === 9
      ? backgroundColor.substr(0, 7)
      : backgroundColor
    : backgroundColor.length === 8
    ? backgroundColor.substr(0, 6)
    : backgroundColor;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    effetiveBackgroundColor
  );
  if (!result) {
    return '#000000'; //Happens when not given hex
  }
  var shade =
    (parseInt(result[1], 16) +
      parseInt(result[2], 16) +
      parseInt(result[3], 16)) /
    3;
  return shade > 128 ? '#000000' : '#FFFFFF';
};

// uncategorized utils
export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const timeToSeconds = (time: string) => {
  const [hour, min, second] = time.split(':');
  return Number(hour) * 3600 + Number(min) * 60 + Number(second);
};

export const secondsToTime = (seconds: number) => {
  const hour = Math.floor(seconds / 3600);
  seconds = seconds - hour * 3600;
  const min = Math.floor(seconds / 60);
  const sec = seconds - min * 60;
  return `${hour.toString().padStart(2, '0')}:${min
    .toString()
    .padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};
